import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  Skeleton,
  Stack,
  IconButton,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from '@mui/styles';
import { theme } from '../../../theme/theme';
import {useGetInternetInvoicesQuery, usePatchPropertyMutation} from '../../../redux/residentPortalApi';
import {formatCurrency} from '../../../utils/currencyUtil';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InternetInvoiceViewModal from './InternetInvoiceViewModal';
import TopUpModal from "../TopUpModal";
import WalletCard from "../WalletCard";
import { useHasAnyRole } from '../../../hooks/useHasAnyRole';
import { useAppDispatch } from '../../../redux/hooks';
import ui from '../../../redux/reducers/uiReducer';
import ResponsiveRecordCard from '../ResponsiveRecordCard';

const useStyles = makeStyles({
  sectionTitle: {
    margin: theme.spacing(4, 0, 2),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  paper: {
    padding: theme.spacing(2),
  },
});


const BillingInformation: React.FC<{propertyId: string; property: any}> = ({propertyId, property}) => {
  const classes = useStyles();
  const showCards = useMediaQuery(theme.breakpoints.down('md'));
  const showInternetBalance = false;
  const {data: invoices, isLoading, isError} = useGetInternetInvoicesQuery({propertyId});
  const visibleInvoices = (invoices || []).filter((row: any) => {
    const status = (row?.status || row?.invoice?.status || '').toString().toLowerCase();
    return status !== 'cancelled';
  });
  const [patchProperty, { isLoading: isSavingWalletBalance }] = usePatchPropertyMutation();
  const canEditInternetWallet = useHasAnyRole([ 'residents-support']);
  const dispatch = useAppDispatch();

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openTopUpDialog, setOpenTopUpDialog] = useState(false);
  const [editWalletOpen, setEditWalletOpen] = useState(false);
  const [editWalletError, setEditWalletError] = useState('');
  const [internetWalletBalance, setInternetWalletBalance] = useState<number>((property?.internetWalletBalance ?? 0) / 100);

  useEffect(() => {
    setPage(0);
  }, [visibleInvoices.length]);

  const paginatedInvoices = useMemo(() => {
    const start = page * rowsPerPage;
    return visibleInvoices.slice(start, start + rowsPerPage);
  }, [visibleInvoices, page, rowsPerPage]);

  const handleOpenTopUp = () => setOpenTopUpDialog(true);
  const handleCloseTopUp = () => setOpenTopUpDialog(false);

  const handleOpenEditWallet = () => {
    setEditWalletError('');
    setInternetWalletBalance((property?.internetWalletBalance ?? 0) / 100);
    setEditWalletOpen(true);
  };

  const handleCloseEditWallet = () => {
    setEditWalletOpen(false);
    setEditWalletError('');
  };

  const handleSaveInternetWalletBalance = async () => {
    if (!property?.id) {
      setEditWalletError('This property cannot be updated because its ID is missing.');
      return;
    }

    try {
      setEditWalletError('');
      await patchProperty({
        propertyPartial: {
          id: property.id,
          internetWalletBalance: Math.round(internetWalletBalance * 100),
        },
      }).unwrap();

      dispatch(ui.actions.addActionNotification({
        show: true,
        notification: {
          title: 'Internet wallet updated',
          description: 'The internet wallet balance was updated successfully.',
        },
      }));

      handleCloseEditWallet();
    } catch (error) {
      setEditWalletError('Failed to update the internet wallet balance. Please try again.');
    }
  };

  const handleOpenView = (row: any) => {
    setSelectedInvoice(row);
    setViewOpen(true);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedInvoice(null);
  };

  return (
    <Box>
      <Typography variant="h4" className={classes.sectionTitle} sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' } }}>
        Internet Invoices
      </Typography>

      {/* Internet Wallet Card */}
      {showInternetBalance && (
        <Box sx={{ mb: 2 }}>
          <WalletCard
            property={property}
            purpose={'internet'}
            handleOpenDialog={handleOpenTopUp}
          />
          {canEditInternetWallet && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button variant="outlined" color="primary" onClick={handleOpenEditWallet}>
                Edit Internet Wallet
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Box>
        {isLoading && (
          <Paper className={classes.paper}>
            <Skeleton height={32} />
            <Skeleton height={32} />
            <Skeleton height={32} />
          </Paper>
        )}
        {isError && (
          <Typography color="error">Failed to load invoices.</Typography>
        )}
        {!isLoading && !isError && visibleInvoices.length === 0 && (
          <Typography color="textSecondary">No internet invoices found for this property.</Typography>
        )}
        {!isLoading && !isError && visibleInvoices.length > 0 && (
          showCards ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {paginatedInvoices.map((row: any, idx: number) => {
                const inv = row.invoice || {};
                const due = inv.dueDate ? new Date(inv.dueDate) : undefined;
                const isOverdue = due ? (due < new Date() && (row.status || inv.status) !== 'Paid') : false;
                const dueStr = due ? due.toLocaleDateString('en-GB', { timeZone: 'UTC' }) : '—';
                const totalDisplay = formatCurrency(inv.total / 100);
                const number = inv.invoiceNumber || inv.id || `Invoice ${idx + 1}`;
                const statusRaw = (row.status || inv.status || '').toString();
                const statusLower = statusRaw.toLowerCase();
                const statusLabel = statusLower === 'paid' ? 'Paid' : statusLower === 'unpaid' ? 'Unpaid' : statusLower === 'overdue' ? 'Overdue' : statusLower === 'due' ? 'Due' : (statusRaw || '—');
                const chipBg = statusLower === 'paid' ? '#4CAF50' : statusLower === 'unpaid' ? '#f44336' : (statusLower === 'due' || statusLower === 'partially paid') ? '#FFBF00' : statusLower === 'overdue' ? '#f44336' : undefined;
                const balanceDisplay = formatCurrency((inv.balance || 0) / 100);

                return (
                  <ResponsiveRecordCard
                    key={number}
                    eyebrow="Internet Invoice"
                    title={number}
                    badge={<Chip label={statusLabel} sx={{ bgcolor: chipBg, color: chipBg ? '#fff' : undefined }} />}
                    fields={[
                      {
                        label: 'Invoice Amount',
                        value: <Typography sx={{ fontWeight: 700 }}>{totalDisplay}</Typography>,
                      },
                      {
                        label: 'Invoice Balance',
                        value: <Typography sx={{ fontWeight: 700 }}>{balanceDisplay}</Typography>,
                      },
                      {
                        label: 'Due Date',
                        value: <Typography sx={{ color: isOverdue ? 'lightcoral' : '#4CAF50', fontWeight: 700 }}>{dueStr}</Typography>,
                      },
                      {
                        label: 'Status',
                        value: <Typography sx={{ fontWeight: 600 }}>{statusLabel}</Typography>,
                      },
                    ]}
                    actions={
                      <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleOpenView(row)}>
                        View
                      </Button>
                    }
                  />
                );
              })}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                   <TableCell>Invoice Number</TableCell>
                    <TableCell>Invoice Amount</TableCell>
                    <TableCell>Invoice Balance</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedInvoices.map((row: any, idx: number) => {
                    const inv = row.invoice || {};
                    const due = inv.dueDate ? new Date(inv.dueDate) : undefined;
                    const isOverdue = due ? (due < new Date() && (row.status || inv.status) !== 'Paid') : false;
                    const dueStr = due ? due.toLocaleDateString('en-GB', { timeZone: 'UTC' }) : '—';
                    const totalDisplay = formatCurrency(inv.total / 100);
                    const number = inv.invoiceNumber || inv.id || `Invoice ${idx + 1}`;
                    const statusRaw = (row.status || inv.status || '').toString();
                    const statusLower = statusRaw.toLowerCase();
                    const statusLabel = statusLower === 'paid' ? 'Paid' : statusLower === 'unpaid' ? 'Unpaid' : statusLower === 'overdue' ? 'Overdue' : statusLower === 'due' ? 'Due' : (statusRaw || '—');
                    const chipBg = statusLower === 'paid' ? '#4CAF50' : statusLower === 'unpaid' ? '#f44336' : (statusLower === 'due' || statusLower === 'partially paid') ? '#FFBF00' : statusLower === 'overdue' ? '#f44336' : undefined;
                    const balanceDisplay = formatCurrency((inv.balance || 0) / 100);

                    return (
                      <TableRow key={number}>
                        <TableCell>{number}</TableCell>
                        <TableCell>{totalDisplay}</TableCell>
                        <TableCell>{balanceDisplay}</TableCell>
                        <TableCell>
                          <Typography style={{ color: isOverdue ? 'lightcoral' : '#4CAF50', fontWeight: 'bold' }}>
                            {dueStr}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={statusLabel} sx={{ bgcolor: chipBg, color: chipBg ? '#fff' : undefined }} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View">
                              <IconButton size="small" aria-label="view invoice" onClick={() => handleOpenView(row)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
        {!isLoading && !isError && visibleInvoices.length > 0 && (
          <TablePagination
            component="div"
            count={visibleInvoices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Box>

      <Dialog open={openTopUpDialog} onClose={handleCloseTopUp} fullWidth maxWidth="sm">
        <DialogContent>
          <TopUpModal purpose="internet" propertyId={propertyId} />
        </DialogContent>
      </Dialog>

      <Dialog open={editWalletOpen} onClose={handleCloseEditWallet} fullWidth maxWidth="sm">
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editWalletError && <Alert severity="error">{editWalletError}</Alert>}
            <TextField
              fullWidth
              label="Internet Wallet Balance"
              type="number"
              value={internetWalletBalance}
              onChange={(event) => setInternetWalletBalance(parseFloat(event.target.value) || 0)}
              inputProps={{ step: '0.01' }}
              helperText="Enter the balance in dollars."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditWallet} disabled={isSavingWalletBalance}>Cancel</Button>
          <Button onClick={handleSaveInternetWalletBalance} variant="contained" color="primary" disabled={isSavingWalletBalance}>
            {isSavingWalletBalance ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedInvoice && (
        <InternetInvoiceViewModal
          open={viewOpen}
          onClose={handleCloseView}
          row={selectedInvoice}
          property={property}
        />
      )}
    </Box>
  );
};

export default BillingInformation;
